import * as Sentry from '@sentry/react';
import { post, get } from './fetchWrapper';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ApplyJobDetails, ApplyJobSubmissionPayload,
  InterviewOfferPreviewOutput,
  InterviewPreviewOutput,
  QuestionsAnswersInput,
  QuestionsOutput,
  StatusInput,
  StatusOutput,
} from './types';
import { generatePath } from 'react-router-dom'
import { endpoints } from '@/constants';

export const api = {
  getStatus: (input: StatusInput & { language: string }) =>
    post<StatusOutput, StatusInput>(endpoints.status, input),
  getQuestions: (input: QuestionsAnswersInput) =>
    post<QuestionsOutput, QuestionsAnswersInput>(
      endpoints.questions,
      input,
    ),
  postAnswersConfirmation: (input: QuestionsAnswersInput) =>
    post<boolean, QuestionsAnswersInput>(
      endpoints.answersConfirmation,
      input,
    ),
  postAnswersUploadRejected: (input: QuestionsAnswersInput) =>
    post<boolean, QuestionsAnswersInput>(
      endpoints.answersUploadRejected,
      input,
    ),
  postInterviewPreview: (input: StatusInput) =>
    post<InterviewPreviewOutput, StatusInput>(
      endpoints.interviewPreview,
      input,
    ),
  getOfferPreview: (input: StatusInput) =>
    get<InterviewOfferPreviewOutput>(`${endpoints.interactions}?invitation_key=${input.invitation_key}`),
  postOfferConfirmed: (input: StatusInput) =>
    post<InterviewPreviewOutput, StatusInput>(endpoints.interactions, input),
  getApplyJobDetails: (property_id: string) =>
    get<ApplyJobDetails>(generatePath(endpoints.qrApplyJob, { property_id })),
  postApplyJobSubmission: (property_id: string, body: ApplyJobSubmissionPayload) =>
    post<ApplyJobDetails>(generatePath(endpoints.qrApplyJob, { property_id }),body),
};

export const useGetStatus = (invitationKey: string | null, language: string) => {
  return useQuery({
    queryKey: ['status', invitationKey],
    queryFn: async () => api.getStatus({ invitation_key: invitationKey, language }),
    enabled: !!invitationKey,
  });
};

export const useGetQuestions = ({
                                  input,
                                  onError,
                                }: {
  input: Omit<QuestionsAnswersInput, 'invitation_key'> & {
    invitation_key: string | null;
  };
  onError?: (err: unknown) => void;
}) => {
  return useQuery({
    queryKey: ['questions', input],
    queryFn: async () => {
      try {
        return await api.getQuestions(input as QuestionsAnswersInput);
      } catch (error) {
        Sentry.captureException(new Error(`getQuestions request failed: ${(error as Error)?.message}`));

        // wait for 3 seconds
        await new Promise((resolve) => {
          setTimeout(() => resolve(true), 3000);
        });

        // retry one more time
        return await api.getQuestions(input as QuestionsAnswersInput);
      }
    },
    enabled: !!input.invitation_key,
    onError: onError,
  });
};

export const useGetQuestionsMutation = ({ onError, onSuccess }) => {
  return useMutation({
    mutationKey: ['questions'],
    mutationFn: async (input: QuestionsAnswersInput) => api.getQuestions(input),
    onError,
    onSuccess,
  });
};

export const usePostAnswersConfirmation = () => {
  return useMutation({
    mutationFn: async (input: QuestionsAnswersInput) => api.postAnswersConfirmation(input),
  });
};

export const usePostAnswersUploadRejected = () => {
  return useMutation({
    mutationFn: async (input: QuestionsAnswersInput) => api.postAnswersUploadRejected(input),
  });
};

export const useInterviewPreview = (input: StatusInput) => {
  return useQuery({
    enabled: !!input?.invitation_key,
    queryKey: ['interviewPreview'],
    queryFn: async () => api.postInterviewPreview(input),
  });
};


export const useInterviewOfferPreview = (input: StatusInput['invitation_key']) => {
  return useQuery({
    enabled: !!input,
    queryKey: ['postOfferPreview'],
    queryFn: async () => api.getOfferPreview({
        invitation_key: input,
    }),
  });
};

export const useInterviewOfferConfirmed = () => {
  return useMutation({
    mutationFn: async (input: StatusInput) => api.postOfferConfirmed(input),
  });
};

export const useApplyJobDetails = (property_id: string) => {
  return useQuery({
    enabled: !!property_id,
    queryKey: ['postOfferPreview'],
    queryFn: async () => api.getApplyJobDetails(property_id),
  });
};

export const useApplyJobSubmission = (property_id: string) => {
  return useMutation({
    mutationFn: async (input: ApplyJobSubmissionPayload) => api.postApplyJobSubmission(property_id, input),
    onSuccess: (response: any) => {
        console.log(response);
    }
  });
};
